import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Rocket, ArrowRight } from 'lucide-react';
import { useSetupProgress } from './useSetupProgress';

/**
 * Compact "finish setting up" banner for the admin dashboard. Data-driven, so it
 * disappears on its own once the essential steps are done — no dismiss needed.
 * Render only for company admins.
 */
export const SetupBanner: React.FC = () => {
  const { tenantSlug } = useParams();
  const path = (p: string) => (tenantSlug ? `/t/${tenantSlug}/app/${p}` : `/app/${p}`);
  const { loading, doneCount, total, allRequiredDone } = useSetupProgress();

  // Hide while detecting and once the essentials are in place.
  if (loading || allRequiredDone) return null;

  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  return (
    <Link
      to={path('admin/setup')}
      className="group flex items-center gap-4 rounded-2xl border border-orange-200 dark:border-orange-500/30 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-500/[0.08] dark:to-amber-500/[0.08] p-4 hover:shadow-md transition-shadow"
    >
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shrink-0">
        <Rocket className="w-5 h-5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-foreground">Finish setting up your company</div>
        <p className="text-sm text-muted-foreground">
          {doneCount} of {total} done — add your team, departments and more.
        </p>
        <div className="h-1.5 rounded-full bg-orange-100 dark:bg-orange-500/20 overflow-hidden mt-2 max-w-xs">
          <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="text-sm font-medium text-orange-600 inline-flex items-center gap-1 shrink-0">
        Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
};

export default SetupBanner;
